import React from 'react';
import SegmentTable from './SegmentTable';
import OptimizationSuggestions from './OptimizationSuggestions';
import { calculatePathDuration } from './utils';

const ComparisonResult = React.memo(function ComparisonResult({ 
  pathDifference, 
  autoPathInfo, 
  customPathInfo, 
  expandedDetails, 
  toggleDetails,
  autoSegments,
  customSegments,
  autoPath,
  customPath,
  locations,
  connections
}) {
  const pointCountDiff = React.useMemo(() => {
    if (autoPathInfo && customPathInfo) {
      return autoPathInfo.pointCount !== customPathInfo.pointCount;
    }
    return false;
  }, [autoPathInfo, customPathInfo]);

  // Calculate duration comparison
  const durationComparison = React.useMemo(() => {
    // Use pre-calculated durations from pathInfo (already considers capacity constraints if enabled)
    if (!autoPathInfo || !customPathInfo) return null;
    
    const autoDuration = autoPathInfo.distance;
    const customDuration = customPathInfo.distance;
    
    if (!autoDuration || !customDuration || autoDuration === 0 || customDuration === 0) return null;
    
    const diff = customDuration - autoDuration;
    const percentDiff = (diff / autoDuration) * 100;
    
    return {
      autoDuration,
      customDuration,
      diff: Math.abs(diff),
      percentDiff: Math.abs(percentDiff),
      isCustomBetter: diff < 0,
      isEffectivelyEqual: Math.abs(percentDiff) < 5 // Less than 5% difference
    };
  }, [autoPathInfo, customPathInfo]);

  const conclusionText = React.useMemo(() => {
    if (!durationComparison) return 'No duration data available for comparison';
    
    // Handle equal paths
    if (durationComparison.isEffectivelyEqual) {
      return 'Both paths have essentially the same duration. Great job!';
    }
    
    return durationComparison.isCustomBetter 
      ? 'Your manual planning path is faster than the algorithm-generated path!' 
      : 'The algorithm-generated path is faster than your manual planning path.';
  }, [durationComparison]);
  
  // Use useMemo to check if we can make a valid comparison
  const canCompare = React.useMemo(() => {
    return autoPathInfo && 
           customPathInfo && 
           typeof autoPathInfo.distance === 'number' && 
           typeof customPathInfo.distance === 'number';
  }, [autoPathInfo, customPathInfo]);

  // Validate paths
  const missingPathsMessage = React.useMemo(() => {
    if (!autoPathInfo && !customPathInfo) {
      return "Both auto and custom paths are required for comparison";
    } else if (!autoPathInfo) {
      return "Auto path is missing or invalid";
    } else if (!customPathInfo) {
      return "Custom path is missing or invalid";
    } else if (typeof autoPathInfo.distance !== 'number') {
      return "Auto path distance information is invalid";
    } else if (typeof customPathInfo.distance !== 'number') {
      return "Custom path distance information is invalid";
    } else if (autoPathInfo.distance <= 0) {
      return "Auto path has zero or negative distance";
    } else if (customPathInfo.distance <= 0) {
      return "Custom path has zero or negative distance";
    }
    return null;
  }, [autoPathInfo, customPathInfo]);

  // Early return after all Hooks
  if (!pathDifference) {
    return (
      <div className="comparison-results error">
        <h5>Comparison Not Available</h5>
        <div className="diff-container">
          <p className="error-message">{missingPathsMessage}</p>
          <p className="error-hint">Please make sure both auto and custom paths are properly created in their respective modes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comparison-results">
      <h5>Comparison Results</h5>
      <div className="diff-container">
        {durationComparison ? (
          <>
            <p className="diff-value">
              <span className={
                durationComparison.isEffectivelyEqual 
                  ? 'equal' 
                  : durationComparison.isCustomBetter ? 'better' : 'worse'
              }>
                {durationComparison.isEffectivelyEqual 
                  ? `Difference: ${durationComparison.diff.toFixed(1)} hours - Negligible`
                  : `${durationComparison.isCustomBetter ? 'Saved ' : 'Added '}${durationComparison.diff.toFixed(1)} hours`
                }
              </span>
            </p>
            <p className="diff-conclusion">
              {conclusionText}
            </p>
          </>
        ) : (
          <p className="no-duration-data">
            No duration data available for comparison. Please create connections in Connection Mode first.
          </p>
        )}
        
        {/* Add detailed path comparison button */}
        <button 
          className="toggle-details-btn"
          onClick={toggleDetails}
        >
          {expandedDetails ? 'Hide Detailed Comparison' : 'Show Detailed Comparison'}
        </button>
        
        {/* Detailed path segment comparison */}
        {expandedDetails && (
          <div className="segments-comparison">
            <h6>Detailed Path Segment Comparison</h6>
            
            {/* Auto path segments table */}
            <SegmentTable 
              segments={autoSegments} 
              totalDuration={durationComparison?.autoDuration || 0} 
              title="Auto Path Segments" 
              className="auto"
              locations={locations}
              connections={connections}
            />
            
            {/* Custom path segments table */}
            <SegmentTable 
              segments={customSegments} 
              totalDuration={durationComparison?.customDuration || 0} 
              title="Custom Path Segments" 
              className="custom"
              locations={locations}
              connections={connections}
            />
            
            {/* Optimization suggestions */}
            {customSegments.length > 0 && !pathDifference.isCustomBetter && (
              <OptimizationSuggestions 
                customSegments={customSegments} 
                autoSegments={autoSegments} 
                isCustomBetter={pathDifference.isCustomBetter}
              />
            )}
          </div>
        )}

      </div>
    </div>
  );
});

export default ComparisonResult; 